import { orderCounterpartyField, orderSearchType, TYPESENSE_ORDER_TYPE } from '@shared/core/search/entities/order';

import type { MultiSearchHit, SearchDocument } from '../../entities/search';

/** The Algolia attribute that carries a palette field on one document, if any does. */
type FieldSource = (document: SearchDocument) => string | undefined;

const isFilled = (value: unknown): boolean => typeof value === 'string' && value.length > 0;

const firstFilled =
  (candidates: readonly string[]): FieldSource =>
  (document) =>
    candidates.find((candidate) => isFilled(document[candidate]));

/**
 * Algolia records name a few fields differently from the Typesense documents the
 * collection configs read. Per collection: palette field → where the Algolia record
 * carries it. An order's counterparty comes from the shared order mapping, so the
 * palette names the same party the order pickers do. The highlight follows the rename.
 */
const FIELD_SOURCES: Readonly<Record<string, Readonly<Record<string, FieldSource>>>> = {
  orders: { counterpartyName: orderCounterpartyField, reference: firstFilled(['customerPO', 'vendorSO']) },
  lots: { lotNumber: firstFilled(['slug']) },
  productionRuns: { reference: firstFilled(['slug']) },
};

/** An Algolia hit in the field vocabulary of the palette's collection configs. */
export function fromAlgoliaHit(collection: string, hit: MultiSearchHit): MultiSearchHit {
  const renames = Object.entries(FIELD_SOURCES[collection] ?? {}).flatMap(([field, sourceOf]) => {
    const source = sourceOf(hit.document);
    return source === undefined ? [] : [[field, source] as const];
  });
  const type = collection === 'orders' ? orderSearchType(hit.document) : undefined;
  const orderType = type === undefined ? undefined : TYPESENSE_ORDER_TYPE[type];

  const document: SearchDocument = {
    ...hit.document,
    id: hit.document.objectID,
    ...Object.fromEntries(renames.map(([field, source]) => [field, hit.document[source]])),
    ...(orderType === undefined ? {} : { orderType }),
  };
  const highlight = {
    ...hit.highlight,
    ...Object.fromEntries(
      renames.flatMap(([field, source]) => {
        const matched = hit.highlight[source];
        return matched === undefined ? [] : [[field, matched]];
      })
    ),
  };
  return { ...hit, document, highlight };
}
